import { SsoConnectionUser } from '@choco-admin/types';
import { formatDateWithPreset } from '@choco-admin/utils';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React from 'react';
import { FiExternalLink } from 'react-icons/fi';

import { useSsoChocoUser } from '../../hooks';
import { userStatusLabel } from '../../utils';

export interface UserDetailDrawerProps {
  user: SsoConnectionUser | null;
  onClose: () => void;
}

const formatDate = (value?: string | number): string =>
  value ? formatDateWithPreset(new Date(value), 'DAY_FIRST') : '—';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="space-y-1">
    <p className="text-xs uppercase tracking-wide text-default-400">{label}</p>
    <div className="break-words text-sm text-foreground">{children}</div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Card shadow="sm">
    <CardBody className="space-y-5 p-5">
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      <Divider />
      {children}
    </CardBody>
  </Card>
);

const ChocoAccountSection: React.FC<{ chocoUserId?: string }> = ({
  chocoUserId,
}) => {
  const { stats, isLoading, error, isMissing, refetch } = useSsoChocoUser({
    chocoUserId,
  });

  if (!chocoUserId) {
    return (
      <p className="text-sm text-default-500">
        This person has signed in through SSO but has no Choco account yet.
      </p>
    );
  }

  if (isLoading) {
    return <Spinner size="sm" />;
  }

  if (error) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-default-500">
          The Choco account could not be loaded.
        </p>
        <Button size="sm" variant="flat" onPress={refetch}>
          Try again
        </Button>
      </div>
    );
  }

  if (isMissing || !stats) {
    return (
      <p className="text-sm text-default-500">
        The linked Choco account no longer exists.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Name">{stats.name ?? '—'}</Field>
        <Field label="Phone number">{stats.phone ?? '—'}</Field>
        <Field label="Distributor">
          {stats.linkedSupplierName ?? 'Not linked to a distributor'}
        </Field>
        <Field label="Customers">{stats.linkedBuyerCount}</Field>
        <Field label="Sign-in method">{stats.loginMethod ?? '—'}</Field>
        <Field label="Account created">{formatDate(stats.createdAt)}</Field>
      </div>

      {stats.pending && (
        <Chip size="sm" variant="flat" color="warning">
          Invitation not yet accepted
        </Chip>
      )}

      <Button
        as="a"
        href={`/users/${chocoUserId}`}
        target="_blank"
        rel="noopener noreferrer"
        size="sm"
        variant="flat"
        endContent={<FiExternalLink />}
      >
        Open user
      </Button>
    </div>
  );
};

export const UserDetailDrawer: React.FC<UserDetailDrawerProps> = ({
  user,
  onClose,
}) => (
  <Drawer isOpen={!!user} onClose={onClose} placement="right" size="5xl">
    <DrawerContent>
      {user && (
        <>
          <DrawerHeader className="flex flex-col gap-1 pr-12">
            <span className="break-all text-base font-semibold">
              {user.email ?? 'SSO user'}
            </span>
          </DrawerHeader>

          <DrawerBody className="space-y-6 pb-8">
            <Section title="Identity provider user data">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Status">
                  <Chip
                    size="sm"
                    variant="flat"
                    color={user.enabled ? 'success' : 'warning'}
                  >
                    {user.enabled ? userStatusLabel(user.status) : 'Disabled'}
                  </Chip>
                </Field>
                <Field label="Sign-in allowed">
                  {user.enabled ? 'Yes' : 'No'}
                </Field>
                <Field label="First signed in">
                  {formatDate(user.createdAt)}
                </Field>
                <Field label="Last updated">
                  {formatDate(user.lastModifiedAt)}
                </Field>
              </div>

              <Field label="Roles">
                {user.federatedRoles.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {user.federatedRoles.map((role) => (
                      <Chip key={role} size="sm" variant="flat">
                        {role}
                      </Chip>
                    ))}
                  </div>
                ) : (
                  <span className="text-default-500">
                    The identity provider sent no roles for this user.
                  </span>
                )}
              </Field>

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-default-400">
                  Attributes sent for this user
                </p>
                {user.mappedAttributes.length > 0 ? (
                  <Table
                    aria-label="Attributes sent for this user"
                    removeWrapper
                    classNames={{
                      th: 'bg-default-100 text-default-700 border border-default-200 font-semibold',
                      td: 'border border-default-200 align-top py-3',
                    }}
                  >
                    <TableHeader>
                      <TableColumn width="32%">
                        Sent by the identity provider
                      </TableColumn>
                      <TableColumn width="26%">
                        Stored on the Choco account
                      </TableColumn>
                      <TableColumn width="42%">Value</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {user.mappedAttributes.map((attribute) => (
                        <TableRow key={attribute.cognitoAttribute}>
                          <TableCell className="break-all font-mono text-xs">
                            {attribute.idpAttribute}
                          </TableCell>
                          <TableCell className="break-all font-mono text-xs">
                            {attribute.cognitoAttribute}
                          </TableCell>
                          <TableCell className="break-all text-xs">
                            {attribute.value ?? (
                              <span className="text-default-400">Not sent</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-default-500">
                    This connection maps no attributes.
                  </p>
                )}
              </div>
            </Section>

            <Section title="Choco user data">
              <ChocoAccountSection chocoUserId={user.chocoUserId} />
            </Section>
          </DrawerBody>
        </>
      )}
    </DrawerContent>
  </Drawer>
);
